import { useCallback, useMemo } from 'react';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type { Account } from '../types';
import { CellContainer, CellTypography } from '../components/AccountsGrid.styles';
import { ArrowUpward, Cancel, CheckCircle } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import { useListParams } from 'hooks';

const columnsAdditionalOptions = {
  disableColumnFilter: true,
  disableColumnMenu: true,
};

export const useAccountsColumns = (): GridColDef[] => {
  const { desc, setDesc, setOrderBy } = useListParams();

  const handleOrderBy = useCallback(() => {
    setDesc(!desc);
    setOrderBy('name');
  }, [desc, setDesc, setOrderBy]);

  return useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Account Name',
        width: 360,
        sortable: false,
        renderHeader: () => {
          return (
            <CellContainer sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant={'subtitle2'}>Account Name</Typography>
              <IconButton
                sx={{ p: 0.5, transform: `rotate(${desc ? 0 : 180}deg)` }}
                onClick={handleOrderBy}
              >
                <ArrowUpward sx={{ fontSize: 16 }} />
              </IconButton>
            </CellContainer>
          );
        },
        renderCell: (params: GridRenderCellParams) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {(params.row as Account).name}
            </CellTypography>
          </CellContainer>
        ),
        ...columnsAdditionalOptions,
      },
      {
        field: 'ad_account_id',
        headerName: 'Account ID',
        width: 360,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {(params.row as Account).ad_account_id}
            </CellTypography>
          </CellContainer>
        ),
        ...columnsAdditionalOptions,
      },
      {
        field: 'app',
        headerName: 'App',
        width: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {(params.row as Account).app}
            </CellTypography>
          </CellContainer>
        ),
        ...columnsAdditionalOptions,
      },
      {
        field: 'platform',
        headerName: 'Platform',
        width: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {(params.row as Account).platform}
            </CellTypography>
          </CellContainer>
        ),
        ...columnsAdditionalOptions,
      },
      {
        field: 'ci_upload',
        headerName: 'Creative media uploads',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {(params.row as Account).ci_upload ? (
                <CheckCircle
                  sx={(t) => ({
                    color: t.palette.success.light,
                    fontSize: t.typography.h4.fontSize,
                  })}
                />
              ) : (
                <Cancel
                  sx={(t) => ({
                    color: t.palette.error.light,
                    fontSize: t.typography.h4.fontSize,
                  })}
                />
              )}
            </CellTypography>
          </CellContainer>
        ),
        ...columnsAdditionalOptions,
      },
      {
        field: 'is_active',
        headerName: 'Active',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {(params.row as Account).is_active ? (
                <CheckCircle
                  sx={(t) => ({
                    color: t.palette.success.light,
                    fontSize: t.typography.h4.fontSize,
                  })}
                />
              ) : (
                <Cancel
                  sx={(t) => ({
                    color: t.palette.error.light,
                    fontSize: t.typography.h4.fontSize,
                  })}
                />
              )}
            </CellTypography>
          </CellContainer>
        ),
        ...columnsAdditionalOptions,
      },
      {
        field: 'black_list_check',
        headerName: 'Blacklist Check',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <CellContainer>
            <CellTypography variant={'body2'}>
              {(params.row as Account).blacklist_check ? (
                <CheckCircle
                  sx={(t) => ({
                    color: t.palette.success.light,
                    fontSize: t.typography.h4.fontSize,
                  })}
                />
              ) : (
                <Cancel
                  sx={(t) => ({
                    color: t.palette.error.light,
                    fontSize: t.typography.h4.fontSize,
                  })}
                />
              )}
            </CellTypography>
          </CellContainer>
        ),
        ...columnsAdditionalOptions,
      },
    ],
    [handleOrderBy, desc],
  );
};
